import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import React, { useState } from 'react'

import CustomerDeleteButton from '../../components/customer/delete-button'
import CustomerModal from '../../components/customer/modal'
import MomentDate from '../../components/date'
import Input from '../../components/input'
import Loading from '../../components/loading'
import Table from '../../components/table'
import Title from '../../components/title'

const GET_CUSTOMERS_WITH_REFERRALS_QUERY = gql`
  query GetCustomersWithReferrals($createdAfter: ISO8601Date, $createdBefore: ISO8601Date) {
    customers(createdAfter: $createdAfter, createdBefore: $createdBefore, limit: 100) {
      id
      email
      firstname
      lastname
      referralOnSignUp
      createdAt
    }
  }
`

const Referrals = () => {
  // Default to last 30 days
  const [createdAfter, setCreatedAfter] = useState(
    moment().subtract(30, 'days').format('YYYY-MM-DD')
  )
  const [createdBefore, setCreatedBefore] = useState(
    moment().format('YYYY-MM-DD')
  )

  const { loading, error, data, refetch } = useQuery(GET_CUSTOMERS_WITH_REFERRALS_QUERY, {
    variables: {
      createdAfter: createdAfter,
      createdBefore: createdBefore
    },
  })

  if (loading) return <Loading />
  if (error) return (
    <div>
      <h2>Fehler:</h2>
      <div>{error}</div>
    </div>
  )

  const customers = data?.customers || []
  const customersWithReferrals = customers.filter(customer => customer.referralOnSignUp)
  const customersWithoutReferrals = customers.filter(customer => !customer.referralOnSignUp)

  // Calculate most used referrals
  const referralCounts = customersWithReferrals.reduce((acc, customer) => {
    const referral = customer.referralOnSignUp
    acc[referral] = (acc[referral] || 0) + 1
    return acc
  }, {})

  const mostUsedReferrals = Object.entries(referralCounts)
    .map(([referral, count]) => ({ referral, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10) // Top 10 most used referrals

  const stats = [
    { name: "Gesamt Kunden", stat: customers.length },
    { name: "Referral ausgefüllt", stat: customersWithReferrals.length },
    { name: "Referral nicht ausgefüllt", stat: customersWithoutReferrals.length },
  ]

  return (
    <div className="">
      <Title>Referrals</Title>

      {/* Time Range Picker */}
      <div className="mb-6 bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Zeitraum</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Von"
            value={createdAfter}
            onChange={setCreatedAfter}
            type="date"
          />
          <Input
            label="Bis"
            value={createdBefore}
            onChange={setCreatedBefore}
            type="date"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="mb-8">
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {stats.map((item) => (
            <div key={item.name} className="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{item.stat}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Most Used Referrals */}
      {mostUsedReferrals.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Meist verwendete Referrals</h3>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-3">
                {mostUsedReferrals.map(({ referral, count }, index) => (
                  <div key={referral} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                      <span className="text-sm text-gray-900 ml-2">{referral}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customers Table */}
      <Table
        loading={loading}
        error={error}
        columns={[
          { label: 'ID', className: "font-semibold text-gray-900" },
          { label: 'Name' },
          { label: 'E-Mail' },
          { label: 'Referral Code' },
          { label: 'Registriert am' },
          { label: 'Aktionen' },
        ]}
        items={customers}
        renderItem={(customer) => {
          return [
            { content: customer.id },
            { content: <CustomerModal id={customer.id} buttonLabel={`${customer.firstname} ${customer.lastname}`} buttonClassName="text-gray-500 hover:underline" /> },
            { content: customer.email },
            { content: customer.referralOnSignUp || '-' },
            { content: <MomentDate date={customer.createdAt} /> },
            {
              content: (
                <CustomerDeleteButton
                  customerId={customer.id}
                  onDeleted={() => refetch()}
                />
              ),
            },
          ]
        }}
      />
    </div>
  )
}

export default Referrals
